import { getCheckoutIntent } from "@/api/rest/checkoutApi";
import { useMachine } from "@xstate/react";
import { createContext, ReactNode, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { checkoutUIMachine } from "./checkoutUIMachine";
import { useAsyncCheckoutEnabled } from "./hooks/useAsyncCheckoutEnabled";

type CheckoutUIContextType = ReturnType<typeof useMachine<typeof checkoutUIMachine>>;

const CheckoutUIContext = createContext<CheckoutUIContextType | null>(null);

interface CheckoutUIProviderProps {
  children: ReactNode;
}

export const CheckoutUIProvider = ({ children }: CheckoutUIProviderProps) => {
  const navigate = useNavigate();
  const fetchOperationRef = useRef<'idle' | 'in-progress' | 'completed'>('idle');

  const searchParams = new URLSearchParams(window.location.search);
  const checkoutIntentId = searchParams.get("checkoutIntentId");

  const { debug } = useAsyncCheckoutEnabled();

  // Create a function to handle navigation that will be passed to the context
  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const machine = useMachine(checkoutUIMachine, {
    input: {
      context: {
        navigate: handleNavigate,
        testOrder: debug,
      },
    },
  });

  const [state, send] = machine;
  const { context } = state;

  // Fetch and set checkout intent if checkoutIntentId is provided
  useEffect(() => {
    const fetchCheckoutIntent = async () => {
      // Skip if we're already fetching or have completed; React strict mode will mount this twice
      // and create the checkout intent twice
      if (fetchOperationRef.current !== 'idle' || state.value === 'initial') {
        return;
      }

      // Skip if we already have this checkout intent
      if (context.checkoutIntent?.checkoutIntentId === checkoutIntentId) {
        fetchOperationRef.current = 'completed';
        return;
      }

      try {
        fetchOperationRef.current = 'in-progress';
        const checkoutIntent = await getCheckoutIntent({ checkoutIntentId });
        if (checkoutIntent) {
          // Send an event to update the context with the checkout intent data
          send({
            type: "SET_CHECKOUT_INTENT",
            data: {
              checkoutIntent,
            }
          });

          // Update the URL params with replace
          navigate(
            `/checkout?checkoutIntentId=${checkoutIntent.checkoutIntentId}`,
            { replace: true }
          );
          fetchOperationRef.current = 'completed';
        }
      } catch (error) {
        console.error("Error fetching checkout intent:", error);
        fetchOperationRef.current = 'idle';
      }
    };

    fetchCheckoutIntent();
  }, [checkoutIntentId, context.checkoutIntent, send, navigate, state.value]);

  return (
    <CheckoutUIContext.Provider value={machine}>
      {children}
    </CheckoutUIContext.Provider>
  );
};

export const useCheckoutUI = () => {
  const context = useContext(CheckoutUIContext);
  if (!context) {
    throw new Error("useCheckoutUI must be used within a CheckoutUIProvider");
  }
  return context;
};
